// lightning-scale.ts — the unified "Lightning Index" (0..1) shared by the OM and
// NWM lightning renderers. The three lightning sources measure DIFFERENT physical
// quantities — HRRR LTNG is flash density (fl/km²), LPI is lightning potential
// (J/kg), WMO weather_code is a categorical present-weather code — so a single
// physical unit can't unify them. Instead we define a 5-rung "thunderstorm
// activity & severity" ladder (0..1) and one mapping function per source onto it,
// so all three render through ONE palette + legend regardless of which source the
// coordinator picked for the current viewport.
//
// This module is intentionally dependency-free (no Colormap / NWM types) so it can
// be imported by the OM colormap, the NWM config, AND the NWM web worker without
// circular imports. The OM Colormap and the NWM ramp are both DERIVED from
// LIGHTNING_LEVELS so there is a single source of truth for the scale.

export interface LightningLevel {
  /** Position on the unified index, 0..1. */
  index: number
  /** RGB 0..255. */
  rgb: [number, number, number]
  /** Alpha 0..255 (None is fully transparent). */
  alpha: number
  /** Legend label. */
  label: string
}

// 5 rungs. The top three are anchored to WMO's real categories (95/96/99); LTNG
// and LPI add the low "Possible" end and grade intensity continuously onto these
// same boundaries. Palette: None transparent → pale yellow → orange → red →
// magenta-purple (matches the existing lightning palettes' hot end).
export const LIGHTNING_LEVELS: LightningLevel[] = [
  { index: 0.0, rgb: [255, 245, 150], alpha: 0, label: 'None' },
  { index: 0.25, rgb: [255, 240, 120], alpha: 140, label: 'Possible' },
  { index: 0.5, rgb: [255, 170, 40], alpha: 205, label: 'Thunderstorm' },
  { index: 0.75, rgb: [240, 70, 50], alpha: 230, label: 'Strong' },
  { index: 1.0, rgb: [156, 39, 176], alpha: 245, label: 'Severe' },
]

// Piecewise-linear interpolation over (input → index) anchor pairs, clamped at the
// ends. `anchors` must be sorted ascending by input.
function piecewise(anchors: readonly [number, number][], x: number): number {
  if (x <= anchors[0][0]) return anchors[0][1]
  const last = anchors[anchors.length - 1]
  if (x >= last[0]) return last[1]
  for (let i = 0; i < anchors.length - 1; i++) {
    const [x0, y0] = anchors[i]
    const [x1, y1] = anchors[i + 1]
    if (x >= x0 && x <= x1) {
      return y0 + (y1 - y0) * ((x - x0) / (x1 - x0))
    }
  }
  return last[1]
}

// HRRR LTNG (flashes/km²) → index. Anchors align with the legacy `ltng` legend
// ticks: Low 0.04, Moderate 0.1, High 0.3.
const LTNG_ANCHORS: readonly [number, number][] = [
  [0, 0],
  [0.04, 0.25],
  [0.1, 0.5],
  [0.3, 0.75],
  [1.0, 1.0],
]
export function ltngToUnified(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 0
  return piecewise(LTNG_ANCHORS, raw)
}

// ICON LPI (J/kg) → index. Standard LPI bands: low <2, moderate 2–5, high 5–10,
// extreme >10.
const LPI_ANCHORS: readonly [number, number][] = [
  [0, 0],
  [1, 0.25],
  [3, 0.5],
  [6, 0.75],
  [10, 1.0],
]
export function lpiToUnified(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 0
  return piecewise(LPI_ANCHORS, raw)
}

// WMO weather_code → index. We map the forecast-derivable "at observation time"
// thunderstorm codes and DELIBERATELY OMIT the observation-only ones (29 =
// thunderstorm in the preceding hour; 91–94 = recent thunderstorm; 13/18/19 =
// visual obs; 98 = TS + dust): a model-derived forecast states the condition AT
// each hour, so those SYNOP past-hour constructs never appear in this data and
// would be inert branches. Open-Meteo emits only 95/96/99 today; 97 (heavy, no
// hail) and 17 (dry thunderstorm) are not emitted yet but are physically
// forecast-plausible, so they're pre-mapped for forward compatibility (zero cost;
// OM currently folds 97 into 95).
//
// Non-thunder codes return 0 (index 0 = alpha 0 → transparent), NOT NaN. Returning
// the in-domain value 0 lets the OM renderer's bilinear value-filtering blend across
// storm/no-storm boundaries (0 → 0.5 fades transparent → pale-yellow → orange),
// softening the otherwise hard categorical cell edges so the layer reads more like
// the continuous ICON/HRRR fields. NaN would instead hit the shader's isnan discard
// and leave blocky 13 km edges. (Genuine missing data is still masked to NaN
// upstream in om-layer-service, so only valid non-storm cells become 0.)
export function weatherCodeToUnified(raw: number): number {
  const code = Math.round(raw)
  if (code === 99) return 1.0 // thunderstorm, heavy, with hail
  if (code === 96 || code === 97) return 0.75 // slight hail / heavy no-hail
  if (code === 95 || code === 17) return 0.5 // ordinary / dry thunderstorm
  return 0
}
